/**
 * Comments polling (spec D3).
 *
 * One 30 s loop per editor page — not per panel tab — so the comment count is
 * fresh whether or not the Comments tab happens to be open, and so opening the
 * tab never costs a spinner. Mount this on the page, once; `CommentsTab` keeps
 * its own mount load, which is idempotent.
 *
 * Two rules make the loop cheap and quiet:
 * - **Hidden tabs don't poll.** A backgrounded editor left open overnight would
 *   otherwise issue ~2,880 requests a night for a list nobody is reading. The
 *   `visibilitychange` listener fires an immediate catch-up refresh on return,
 *   so the user never waits out the remainder of an interval they didn't see.
 * - **Ticks are silent** — no loading flag, no error banner, last-good data
 *   kept on failure. See `load`'s `silent` option.
 *
 * The loop never *opens* a view session — only the panel may decide the user is
 * looking at the Comments tab. But once one is open it owns the re-fire (spec
 * D4): after every successful tick it re-POSTs `comments-viewed`, so comments
 * that arrive while the reader sits on the tab are marked seen without waiting
 * for them to leave and come back. The panel fires only on activation, so there
 * is exactly one owner for each edge.
 */

import { useEffect } from 'react'
import { useCommentsStore } from '@/stores/comments-store'
import { useAgentStore } from '@/stores/agent-store'
import { hasViewSession } from '@/lib/comment-unread'
import type { CommentTargetType } from '@/lib/comments-api'

const POLL_INTERVAL_MS = 30_000

/**
 * All of these must hold for the watermark to advance: the comments surface is
 * the live view (it opened a snapshot session) and this browser tab is on
 * screen. Anything less isn't a person reading, and advancing on it would close
 * the counterparty's edit window behind their back.
 *
 * A draft adds one more gate — the AI panel must be expanded, since that panel
 * is where the Comments tab lives. An extraction thread (a compliance
 * requirement, a risk-register item) lives in its own row drawer instead, so
 * applying the panel gate there would mean the normal case (drawer open, AI
 * panel collapsed) never advanced its cut-line at all.
 *
 * Read straight from the stores rather than the panel's `tab`: readers get a
 * forced comments-only panel that deliberately leaves the persisted tab
 * preference alone, so the session key is the only honest signal.
 */
function isCommentsViewLive(targetId: string, targetType: CommentTargetType): boolean {
  if (document.visibilityState !== 'visible') return false
  if (targetType === 'draft' && !useAgentStore.getState().isPanelOpen) return false
  return hasViewSession(useCommentsStore.getState(), targetId)
}

export function useCommentsPolling(
  bidId: string | undefined,
  targetId: string | undefined,
  opts?: { targetType?: CommentTargetType },
): void {
  const load = useCommentsStore((s) => s.load)
  const targetType = opts?.targetType ?? 'draft'

  useEffect(() => {
    if (!bidId || !targetId) return

    // First load is loud: this one may legitimately show a spinner or an error.
    void load(bidId, targetId, 'all', { targetType })

    const tick = async (): Promise<void> => {
      if (document.visibilityState !== 'visible') return
      await load(bidId, targetId, 'all', { silent: true, targetType })
      // Re-checked after the await: the user may have closed the comments
      // surface or left the tab while the request was in flight.
      if (!isCommentsViewLive(targetId, targetType)) return
      await useCommentsStore.getState().markViewed(bidId, targetId, targetType)
    }

    const refresh = (): void => {
      void tick()
    }

    const intervalId = setInterval(refresh, POLL_INTERVAL_MS)
    document.addEventListener('visibilitychange', refresh)

    return () => {
      clearInterval(intervalId)
      document.removeEventListener('visibilitychange', refresh)
    }
  }, [bidId, targetId, targetType, load])
}
